import React, { useState } from 'react';
import { Box, Text, Button, Flex, Image } from 'rebass';
import { useWindowSize } from 'react-use';
import { Formik, Form, Field } from 'formik';
import TextField from 'app/form/formik/text/index';
import { AiOutlineEdit } from "react-icons/ai";
import DateField from 'app/form/formik/date/index';
import { patch, post } from 'utils/api';
import * as Yup from 'yup';
import CheckMark from 'assets/svg/check-mark';
import { useHistory } from 'react-router-dom';
import { ErrorMessageWrap } from 'app/form/formik/text/styled';

const Star = () => <svg xmlns="http://www.w3.org/2000/svg"
    className="glowing-star"
    width="31" height="30" viewBox="0 0 31 30" fill="none">
    <path d="M12.6468 2.78115C13.5449 0.0172229 17.4551 0.0172186 18.3532 2.78115L19.878 7.47417C20.2796 8.71024 21.4315 9.54712 22.7312 9.54712H27.6657C30.5719 9.54712 31.7802 13.266 29.4291 14.9742L25.437 17.8746C24.3855 18.6385 23.9455 19.9926 24.3471 21.2287L25.872 25.9217C26.7701 28.6857 23.6066 30.984 21.2555 29.2758L17.2634 26.3754C16.2119 25.6115 14.7881 25.6115 13.7366 26.3754L9.74453 29.2758C7.39339 30.984 4.22995 28.6857 5.128 25.9217L6.65285 21.2287C7.05448 19.9926 6.6145 18.6385 5.56304 17.8746L1.57092 14.9742C-0.780216 13.266 0.428109 9.54712 3.33428 9.54712H8.26881C9.56849 9.54712 10.7204 8.71024 11.122 7.47417L12.6468 2.78115Z" fill="url(#paint0_linear_7720_64327)" />
    <defs>
        <linearGradient id="paint0_linear_7720_64327" x1="2.5" y1="-6" x2="29.5" y2="35" gradientUnits="userSpaceOnUse">
            <stop offset="0.00480769" stop-color="#FFB31F" />
            <stop offset="0.336538" stop-color="#FFF2D9" />
            <stop offset="0.807692" stop-color="#FFB31F" />
            <stop offset="0.971154" stop-color="#996B13" />
        </linearGradient>
    </defs>
</svg>

const EditMilestone = ({ milestone, revalidate, journalId, setMileStoneEdit, onAllMilestones }) => {
    const [step, setStep] = useState(1);
    const { width } = useWindowSize();
    const [istitleEditing, setIsEditing] = useState(false);
    const [message, setMessage] = useState(null);

    const { push } = useHistory();

    // Toast state for step 2
    const [showToast, setShowToast] = useState(false);

    const handleNext = () => {
        setStep(2);
        setShowToast(true);
    };

    const isMobile = width < 600;
    const buttonPlacement = isMobile ? 'bottom' : 'top';
    const options = { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' };

    let isoDate = milestone.startDate ? new Date(milestone.startDate).toISOString() : null;
    let formattedDate = '';

    if (isoDate) {
        const [year, month, day] = (isoDate || '').split('T')[0].split('-'); // Split into "YYYY", "MM", "DD"
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        formattedDate = `${monthNames[parseInt(month, 10) - 1]} ${day}, ${year}`;
    } else {
        console.log('No valid date available');
    }

    return (
        <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4, position: 'relative' }}>

            {step === 1 && (
                <Formik
                    initialValues={{
                        'start-date': milestone.startDate || new Date(),
                        title: milestone.title || '',
                    }}
                    validationSchema={Yup.object({
                        title: Yup.string()
                            .test(
                                "max-length",
                                "Title must be 36 characters or fewer.",
                                (value) => {
                                    const textOnly = String(value || "").replace(/<[^>]*>/g, '').replace(/\s+/g, '').trim();
                                    const characterCount = textOnly.length;
                                    return characterCount <= 36; // Ensure the length is within the limit
                                }
                            )
                    })}
                    onSubmit={async values => {
                        if (new Date(values?.['start-date']).toISOString() !== new Date(milestone.startDate).toISOString() || values?.title !== milestone.title) {

                            const { data, status } = await patch(`/v3/child_journals/${journalId}/milestones/${milestone?.id}`, {
                                data: {
                                    attributes: values,
                                },
                            });
                            if (status === 200) {
                                if (milestone.complete)
                                    setMessage('Milestone updated!');
                                else {
                                    setMessage('New Milestone added to book!');
                                }
                                setMileStoneEdit({
                                    id: data?.initial?.data?.attributes?.id,
                                    startDate: data?.initial?.data?.attributes?.['start-date'],
                                    statusIcon: data?.initial?.data?.attributes?.['status-icon'],
                                    title: data?.initial?.data?.attributes?.title,
                                    complete: data?.initial?.data?.attributes?.complete,
                                    storyId: data?.initial?.data?.relationships?.['story']?.data?.id,
                                })
                                revalidate()
                            }
                            handleNext()
                        } else {
                            setStep(2);
                        }
                    }}

                >
                    {({ values, handleSubmit, errors, setFieldValue, ...props }) => {

                        return (

                            <Form>
                                <Flex justifyContent="space-between" sx={{
                                    position: buttonPlacement === 'bottom' ? 'fixed' : 'static',
                                    bottom: buttonPlacement === 'bottom' ? 16 : 'unset',
                                    width: '100%',
                                    right: 1,
                                }}>
                                    <Button variant="outline" onClick={onAllMilestones}
                                        sx={{
                                            borderRadius: '23px',
                                            height: 44,
                                            border: 'transparent',
                                            background: 'transparent',
                                            color: '#0E0E0E'
                                        }}
                                    >Back</Button>
                                    <Button type="submit" onClick={handleSubmit} py="unset" sx={{
                                        borderRadius: '23px',
                                        height: 44,
                                        border: '1px solid #9B59B6',
                                        background: '#9B59B6',
                                    }}>{milestone?.complete ? 'Update Milestone' : 'Add Milestone'}</Button>
                                </Flex>
                                {buttonPlacement === 'top' && <Box my={4} sx={{ border: '0.5px solid #E2E8F0' }} />}
                                <Flex flexDirection="column" alignItems="center">
                                    <Image
                                        src={milestone?.statusIcon || 'https://via.placeholder.com/150'}
                                        width={103}
                                    />
                                    <Box my={3}>
                                        {istitleEditing ? <Box width="100%" minWidth={300} maxWidth={500}>
                                            <Field
                                                component={TextField}
                                                name="title"
                                                showError
                                                width="100%"
                                                mt={2}
                                                height={87}
                                                autoFocus
                                            />

                                            <Flex justifyContent="space-around">
                                                <Button height={44} onClick={() => setIsEditing(false)} sx={{ borderRadius: '23px', py: 'unset' }} bg="#9B59B6">Confirm</Button>
                                                <Button onClick={() => {
                                                    setIsEditing(false)
                                                    setFieldValue('title', milestone.title || '');
                                                }} height={44} sx={{ borderRadius: '23px', py: 'unset' }} bg="#737373">Cancel</Button>
                                            </Flex>
                                        </Box>
                                            :
                                            <Flex onClick={() => setIsEditing(true)} maxWidth={293} m="auto" flexDirection="column">
                                                <Text color='#0E0E0E' textAlign="center" fontFamily='Bitter' fontSize={26} letterSpacing='0.572px' >{values.title}
                                                    <Text as={AiOutlineEdit} color="#9B59B6" size={24} ml={1} sx={{ cursor: 'pointer', minWidth: '24px' }} />
                                                </Text>
                                                {errors?.title && <ErrorMessageWrap maxWidth="max-content" fontSize={16}>
                                                    {errors?.title}
                                                </ErrorMessageWrap>}
                                            </Flex>}
                                    </Box>
                                    <Box mb={3}>
                                        <Text mb={1} color='#0E0E0E' fontFamily='Poppins' fontSize={22} letterSpacing='-0.484px' lineHeight={'120%'}>
                                            When did this milestone occur?
                                        </Text>
                                        <Text mb="30px" color='#737373' fontFamily='Poppins' fontSize={16} letterSpacing='-0.352px' lineHeight={'150%'}>Estimates are fine, you can change it later.</Text>

                                        <Field
                                            component={DateField}
                                            name="start-date"
                                            width="100%"
                                            mt={2}
                                            menuPlacement="bottom"
                                        />

                                    </Box>
                                </Flex>

                            </Form>
                        )
                    }}
                </Formik>
            )}

            {step === 2 && (<Box>
                <Flex justifyContent="end" sx={{
                    position: buttonPlacement === 'bottom' ? 'fixed' : 'static',
                    bottom: buttonPlacement === 'bottom' ? 0 : 'unset',
                    py: buttonPlacement === 'bottom' ? 16 : 'unset',
                    width: '100%',
                    bg: buttonPlacement === 'bottom' && 'white',
                    right: 1,

                }}>
                    <Button width="max-content" height={44} py="unset" sx={{ borderRadius: '23px' }} onClick={onAllMilestones}>
                        Back to Milestones {'>'}
                    </Button>
                </Flex>
                {buttonPlacement === 'top' && <Box my={4} sx={{ border: '0.5px solid #E2E8F0' }} />}
                <Flex flexDirection="column" alignItems="center" mt={5}>
                    <Image
                        src={milestone?.statusIcon || 'https://via.placeholder.com/150'}
                        width={252}
                    />
                    {/* <Flex sx={{ position: 'absolute' }}> */}
                    <Box sx={{ position: 'absolute', mt: isMobile ? '-8%' : '-6%' }} >{Star()} </Box>
                    <Box sx={{ position: 'absolute', ml: isMobile ? '-60%' : '-50%', mt: '5%' }} >{Star()} </Box>
                    <Box sx={{ position: 'absolute', ml: isMobile ? '60%' : '50%', mt: '5%' }} >{Star()} </Box>
                    <Box sx={{ position: 'absolute', ml: isMobile ? '50%' : '40%', mt: isMobile ? '50%' : '40%' }} >{Star()} </Box>
                    <Box sx={{ position: 'absolute', ml: isMobile ? '-50%' : '-40%', mt: isMobile ? '50%' : '40%' }} >{Star()} </Box>
                    {/* </Flex> */}
                    <Text mt={4} color='#0E0E0E' textAlign="center" fontFamily='Bitter' fontSize={26} letterSpacing='-0.572px' >
                        {milestone.title}
                    </Text>
                    <Text mt={4} color='#0E0E0E' fontWeight={400} fontFamily='Poppins' fontSize={22} letterSpacing='-0.572px' lineHeight={'24px'} >
                        Achieved on {formattedDate}
                    </Text>
                    {showToast && (
                        <Flex
                            mt={3}
                            justifyContent="center"
                            alignItems="center"
                            width="max-content"
                            sx={{
                                bg: '#E0FFEA',
                                color: 'white',
                                py: '10px',
                                px: 2,
                                borderRadius: '30px',
                            }}
                        >
                            <CheckMark fill="#00611F" />&nbsp;  <Text fontWeight="600px" color="#00611F">{message}</Text>
                        </Flex>
                    )}
                    {milestone?.storyId ?
                        <Button width="max-content" mb={[8, 3]} mt={3} sx={{ borderRadius: '30px', }} variant="outline"
                            onClick={() => {
                                const pathname = `/story/${milestone?.storyId}/edit`;
                                push({ pathname });
                            }}
                        >
                            Edit Milestone Story
                        </Button> : <Button width="max-content" my={3} sx={{
                            borderRadius: '30px',
                        }}
                            variant="outline"
                            onClick={async () => {
                                const { data, status } = await post(`v3/stories?milestone_id=${milestone.id}`)
                                if (status === 200)
                                    setMileStoneEdit({
                                        ...milestone,
                                        storyId: data?.initial?.data?.attributes?.id,
                                    })

                                push(`/story/${data?.initial?.data?.attributes?.id}/edit?add-to-story=true`);
                            }}
                        >
                            Start a Story +
                        </Button>}
                </Flex>
            </Box>
            )
            }
        </Box >
    );
};

export default EditMilestone;